import { Play } from "lucide-react";

// The pieces the escalation ladder is drawn with, in the same look as the Service journey preview
// (JourneyPreview.jsx): numbered nodes on a dotted canvas, grey arrows between them, and a dashed
// arrow where something other than the clock starts the next step (here, the customer's answer).

/** The dotted canvas the whole ladder sits on. */
export function LadderCanvas({ children }) {
  return (
    <div className="jp-canvas rounded-2xl border border-[#EAECF0] bg-[#FCFCFD] px-4 py-8 md:px-8" data-testid="ladder-canvas">
      <div className="mx-auto flex w-full max-w-[760px] flex-col items-stretch">{children}</div>
    </div>
  );
}

/** Where the ladder begins. */
export function StartNode({ label = "Ladder starts" }) {
  return (
    <div className="flex justify-center" data-testid="ladder-start">
      <span className="inline-flex items-center gap-[6px] rounded-full border border-[#D0D5DD] bg-white px-3 py-[5px] text-[12px] font-semibold text-[#344054] shadow-[0_1px_2px_rgba(16,24,40,0.06)]">
        <Play size={11} className="text-[#2667B5]" aria-hidden="true" />
        {label}
      </span>
    </div>
  );
}

/** An arrow down to the next node: solid when the clock sends it on, dashed when something else does. */
export function Edge({ dashed = false, label = "" }) {
  const colour = dashed ? "#C1C7D0" : "#98A2B3";
  return (
    <div className="flex flex-col items-center" aria-hidden="true" data-edge={dashed ? "dashed" : "solid"}>
      <svg width="14" height={label ? 38 : 30} viewBox={`0 0 14 ${label ? 38 : 30}`} className="overflow-visible">
        <line x1="7" y1="0" x2="7" y2={label ? 31 : 23} stroke={colour} strokeWidth="1.5" strokeDasharray={dashed ? "4 4" : undefined} />
        <path d={`M2.5,${label ? 28 : 20} L7,${label ? 36 : 28} L11.5,${label ? 28 : 20} z`} fill={colour} />
      </svg>
    </div>
  );
}

/**
 * One step as a node: a numbered badge (round for a step, rounded square for a question, as custom
 * stages are drawn in the journey), its name and a one-line summary, the step's controls underneath.
 *
 *   icon      lucide icon for the summary line      summary   the line beside it
 *   badge     something to show in the badge instead of the number (the first message has an envelope)
 *   off       the step is switched off (dashed)      actions   toggle and remove, top right
 */
export function NodeShell({ index = 0, badge = null, question = false, title, icon: Icon, summary, missing = false, off = false, actions, children, testId }) {
  return (
    <div
      data-testid={testId}
      className={`jp-node rounded-xl border bg-white px-4 py-3 shadow-[0_1px_2px_rgba(16,24,40,0.06)] transition-[border-color,box-shadow] duration-200 hover:border-[#98A2B3] hover:shadow-[0_6px_14px_-6px_rgba(16,24,40,0.18)] ${
        off ? "border-dashed border-[#D0D5DD] opacity-75" : "border-[#D0D5DD]"
      }`}
      style={{ animationDelay: `${Math.min(index, 12) * 35}ms` }}
    >
      <div className="flex items-center gap-[10px]">
        <span
          className={`flex h-6 w-6 flex-none items-center justify-center text-[11.5px] font-semibold tabular-nums text-white ${question ? "rounded-[7px]" : "rounded-full"} ${off ? "bg-[#98A2B3]" : "bg-[#2667B5]"}`}
        >
          {badge || index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13.5px] font-semibold text-[#101828]">{title}</div>
          <div className={`mt-[2px] flex items-center gap-[6px] text-[11.5px] ${missing ? "text-[#B54708]" : "text-[#667085]"}`}>
            {Icon && <Icon size={12} className="flex-none" aria-hidden="true" />}
            <span className="truncate">{summary}</span>
          </div>
        </div>
        {actions}
      </div>
      <div className="mt-3 flex flex-col gap-3 border-t border-[#F2F4F7] pt-3">{children}</div>
    </div>
  );
}
